import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type ReactElement,
} from "react";
import { useCss, a } from "kremling";
import Tippy from "@tippyjs/react";
import type { Instance } from "tippy.js";
import { useIsMounted } from "@hooks";

import {
  getAnimationDuration,
  useAutoDisable,
  useContentWidth,
  hideOnEsc,
  useDropdownPlacementConversion,
} from "../cp-dropdown/cp-dropdown.utils";
import type { CpDropdownProps } from "../cp-dropdown/cp-dropdown.component";
import styles from "../cp-dropdown/cp-dropdown.styles.scss";
import { createPortal } from "react-dom";
import { clickEventStack, elementHasClass } from "@helpers";

const animationDuration = getAnimationDuration();

type RenderContentProps = {
  isOpen: boolean;
  close: () => void;
};

export type CpContextDropdownProps = {
  allowContentClicks?: boolean;
  appendTo?: Element | "parent" | ((ref: Element) => Element);
  backdrop?: boolean;
  contentHeight?: string | number;
  contentWidth?: CpDropdownProps["contentWidth"];
  disabled?: boolean;
  position?: CpDropdownProps["position"];
  onClose?: () => void;
  onOpen?: () => void;
  renderContent: (props: RenderContentProps) => ReactNode;
};

export type CpContextDropdownRef = {
  open: (rect?: Partial<DOMRect>) => void;
  close: () => void;
  toggle: () => void;
  isOpen: boolean;
};

/**
 * @deprecated Use CpFloatingMenu with contextTrigger instead
 */
export const CpContextDropdown = forwardRef<
  CpContextDropdownRef,
  CpContextDropdownProps
>(function CpContextDropdown(props, ref) {
  const {
    allowContentClicks = props.backdrop || props.allowContentClicks,
    appendTo = document.body,
    backdrop,
    contentHeight = "auto",
    contentWidth = "sm",
    disabled,
    position = "bottom",
    onClose,
    onOpen,
    renderContent,
  } = props;
  const scope = useCss(styles);
  const [isOpen, setIsOpen] = useState(false);
  const unmountTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const eventStackRef = useRef<{ remove: () => void } | null>(null);
  const [instance, setInstance] = useState<Instance | null>(null);
  const [refEl, setRefEl] = useState<ReactElement>(<div />);
  const isMounted = useIsMounted();
  const calculatedContentWidth = useContentWidth(contentWidth);
  const placement = useDropdownPlacementConversion(position);

  // todo: Add missing deps and verify it doesn't break: isOpen, close
  useEffect(() => {
    if (disabled && isOpen) close();
  }, [disabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (eventStackRef?.current) eventStackRef.current.remove();
    };
  }, []);

  function onMount() {
    if (unmountTimeoutRef.current) {
      clearTimeout(unmountTimeoutRef.current);
    }
  }

  function onShow() {
    setIsOpen(true);
    if (onOpen) onOpen();
    setTimeout(() => {
      eventStackRef.current = clickEventStack.add(documentClickHandler);
    }, 0);
  }

  function onHide({ unmount }: { unmount: () => void }) {
    if (isMounted.current) {
      setIsOpen(false);
      if (onClose) onClose();
    }
    if (eventStackRef?.current) eventStackRef.current.remove();
    // wait for animation to finish before unmounting
    unmountTimeoutRef.current = setTimeout(() => {
      if (instance && isMounted.current) {
        unmount();
      }
    }, animationDuration);
  }

  function open(rect: Partial<DOMRect> = {}) {
    setRefEl(
      <div
        style={{
          position: "absolute",
          height: 0,
          width: 0,
          ...rect,
        }}
      />,
    );
    if (instance) {
      instance.show();
    }
    if (onOpen) onOpen();
  }

  function close() {
    if (instance) instance.hide();
    if (onClose) onClose();
  }

  function toggle() {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }

  if (ref) {
    const current: CpContextDropdownRef = {
      open,
      close,
      toggle,
      isOpen,
    };
    if (typeof ref === "function") {
      ref(current);
    } else {
      ref.current = current;
    }
  }

  useAutoDisable(disabled, isOpen, close);

  // todo: Add missing deps and verify it doesn't break: close
  const documentClickHandler = useCallback(
    (e: MouseEvent) => {
      if (!instance) return;
      const target = e.target as HTMLElement;
      const contains =
        instance.popper.contains(target) ||
        elementHasClass(target, ["cp-dropdown-backdrop", "cp-dropdown"]);
      const isTrigger = instance.reference.contains(target);
      if (isTrigger) return;
      if ((contains && !allowContentClicks) || !contains) {
        close();
      }
    },
    [instance, allowContentClicks], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <Tippy
      allowHTML
      animation
      appendTo={appendTo}
      disabled={disabled}
      interactive
      hideOnClick={false}
      onCreate={setInstance}
      offset={[0, 0]}
      onHide={onHide}
      onShow={onShow}
      onMount={onMount}
      placement={placement}
      plugins={[hideOnEsc]}
      trigger="click"
      zIndex={100001}
      render={(attrs) => {
        return (
          <>
            {!!backdrop &&
              isOpen &&
              createPortal(
                <div
                  {...scope}
                  data-testid="backdrop"
                  className="cp-dropdown-backdrop"
                />,
                document.body,
              )}
            <div
              {...attrs}
              {...scope}
              className={a("cp-dropdown").m("cp-dropdown--is-open", isOpen)}
              tabIndex={-1}
              onClick={() => {
                if (!backdrop && !allowContentClicks) {
                  close();
                }
              }}
              style={{
                maxHeight: contentHeight,
                width: calculatedContentWidth,
              }}
            >
              {renderContent({ isOpen, close })}
              <div className="cp-tooltip-arrow" data-popper-arrow="" />
            </div>
          </>
        );
      }}
    >
      {refEl}
    </Tippy>
  );
});
